import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useSubmittedComparison } from 'hooks/api/useDashboard';

interface Props {
  managerId?: string;
  weekStartDate?: string;
}

function formatWeekLabel(isoDate: string): string {
  if (!isoDate) return '';
  const parts = isoDate.split('T')[0].split('-').map(Number);
  if (parts.length < 3) return isoDate;
  const d = new Date(parts[0], parts[1] - 1, parts[2]);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]}`;
}

const SubmittedTimesheetsWidget: React.FC<Props> = ({ managerId, weekStartDate }) => {
  const { data, loading } = useSubmittedComparison(managerId, weekStartDate);

  const total = data?.totalReportees ?? 0;
  const change = data?.changePercent ?? 0;
  const isUp = change >= 0;
  const weeks = data?.recentWeeks ?? [];

  return (
    <div className="dashboard-widget">
      <div className="dashboard-widget-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <IconSystem.checkCircle size={18} />
          <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Submission Trend</h3>
        </div>
        {!loading && weeks.length > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            color: isUp ? '#16a34a' : '#dc2626',
            fontSize: '0.75rem', fontWeight: 600,
          }}>
            {isUp ? <IconSystem.trendingUp size={14} /> : <IconSystem.trendingDown size={14} />}
            {isUp ? '+' : ''}{change}%
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          Loading...
        </div>
      ) : weeks.length === 0 ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
          No data.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            Active reportees (eligible per week) — last 4 weeks
          </div>
          {weeks.map((w, idx) => {
            const eligible = w.eligibleCount ?? total;
            const pct = eligible > 0 ? (w.submittedCount / eligible) * 100 : 0;
            const isCurrentSelection = idx === 0;
            const weekLabel = isCurrentSelection ? 'Selected' : idx === 1 ? 'Prev' : `-${idx}w`;
            return (
              <div key={w.weekStartDate} style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 60px',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.75rem',
              }}>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <div style={{ fontWeight: isCurrentSelection ? 700 : 500 }}>{weekLabel}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{formatWeekLabel(w.weekStartDate)}</div>
                </div>
                <div style={{
                  height: 14,
                  background: 'var(--bg-secondary, #f1f5f9)',
                  borderRadius: 7,
                  overflow: 'hidden',
                  display: 'flex',
                }}>
                  <div style={{
                    width: `${pct}%`,
                    background: pct >= 80 ? '#16a34a' : pct >= 50 ? '#d97706' : '#dc2626',
                    transition: 'width 0.3s ease',
                  }} />
                </div>
                <div style={{ textAlign: 'right', fontWeight: 600 }}>
                  {w.submittedCount} / {eligible}
                </div>
              </div>
            );
          })}
          {weeks.length > 0 && (
            <div style={{
              borderTop: '1px solid var(--border-color)',
              marginTop: '0.25rem',
              paddingTop: '0.4rem',
              fontSize: '0.7rem',
              color: 'var(--text-secondary)',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <span>Submitted</span>
              <span>Draft: {weeks[0]?.draftCount || 0} &middot; Not Started: {weeks[0]?.notStartedCount || 0}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubmittedTimesheetsWidget;
